import { useEffect } from 'react';

interface ImagePreloaderProps {
  images: string[];
  priority?: boolean;
}

/**
 * Preloads images to improve LCP and reduce layout shifts
 * Uses high priority for critical images and low priority for others
 */
export function ImagePreloader({ images, priority = false }: ImagePreloaderProps) {
  useEffect(() => {
    const preloadImages = () => {
      images.forEach((src, index) => {
        // Create link element for preloading
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = src;
        
        // Set priority for critical images (first image or when priority is true)
        if (priority || index === 0) {
          link.setAttribute('fetchpriority', 'high');
        } else {
          link.setAttribute('fetchpriority', 'low');
        }
        
        // Add to document head
        document.head.appendChild(link);
        
        // Also create Image object for browser cache
        const img = new Image();
        img.src = src;
        
        // Optional: Add loading event listener for performance tracking
        img.onload = () => {
          console.debug(`Image preloaded: ${src}`);
        };
        
        img.onerror = () => {
          console.warn(`Failed to preload image: ${src}`);
        };
      });
    };

    // Use requestIdleCallback for non-critical preloading
    if ('requestIdleCallback' in window && !priority) {
      requestIdleCallback(preloadImages);
    } else {
      // For critical images or browsers without requestIdleCallback
      preloadImages();
    }
  }, [images, priority]);

  return null; // This component doesn't render anything
}

export default ImagePreloader;